/* ==========================================================================
   SŌL COLLECTIVE — REVAMP BEHAVIOUR (Sept 2026)

   Deliberately additive. Nothing in sections/header.liquid or
   layout/theme.liquid is modified, so reverting is a matter of removing
   sections/sol-boot.liquid from the header group.

   1. Menu drawer, to wireframe 3b.
   2. Parent menu rows expand instead of navigating.
   3. Search icon beside the hamburger; account icon beside the cart.
   4. Search drawer placeholder copy.
   5. Shop Pay instalments line moved directly under the buy button.
   6. Rep AI question widget moved out from under the product gallery, to just
      below the instalments line.
   7. Brand Reviews section hidden when the brand has no reviews.
   8. Sticky add to cart on mobile product pages.
   ========================================================================== */

(function () {
  'use strict';

  var SEARCH_PLACEHOLDER = "let's make this easier...";

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function el(tag, cls, html) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (html != null) node.innerHTML = html;
    return node;
  }

  function rootUrl() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  /* ------------------------------------------------------------------------
     1. MENU DRAWER
     ------------------------------------------------------------------------ */

  function buildDrawer() {
    var drawer = document.getElementById('main-menu');
    if (!drawer || drawer.dataset.solEnhanced === 'true') return;

    var inner = drawer.querySelector('.main-menu-inner');
    if (!inner) return;

    drawer.dataset.solEnhanced = 'true';

    /* --- head: wordmark + close ------------------------------------------ */
    var head = el('div', 'sol-drawer__head');

    var wordmark = el('a', 'sol-drawer__wordmark');
    wordmark.href = rootUrl();
    var logo = document.querySelector('.page-header .logo__default-image, .page-header .logo img');
    if (logo) {
      var img = document.createElement('img');
      img.src = logo.currentSrc || logo.src;
      img.alt = logo.alt || '';
      wordmark.appendChild(img);
    } else {
      wordmark.appendChild(el('span', 'sol-drawer__wordmark-text', document.title.split(/[|\-–]/)[0].trim()));
    }

    var close = el('button', 'sol-drawer__close');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close menu');
    close.innerHTML =
      '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<path d="M1 1L15 15M15 1L1 15" stroke="currentColor" stroke-width="1.4"/></svg>';
    close.addEventListener('click', function () {
      // The drawer is a <details> disclosure: closing it means re-triggering
      // the same toggle the customer tapped to open it.
      var disclosure = document.getElementById('menu-disclosure');
      var toggle = disclosure && disclosure.querySelector('summary .menu-toggle');
      if (toggle) toggle.click();
      else if (disclosure) disclosure.open = false;
    });

    head.appendChild(wordmark);
    head.appendChild(close);

    /* --- search, pinned to the top --------------------------------------- */
    var form = el('form', 'sol-drawer__search');
    form.action = rootUrl().replace(/\/$/, '') + '/search';
    form.method = 'get';
    form.setAttribute('role', 'search');
    form.innerHTML =
      '<span class="sol-drawer__search-icon">' +
      '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
      '<circle cx="7" cy="7" r="5.25" stroke="currentColor" stroke-width="1.3"/>' +
      '<path d="M11 11L15 15" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/></svg></span>' +
      '<label class="visually-hidden" for="sol-drawer-search">Search</label>' +
      '<input type="search" id="sol-drawer-search" name="q" autocomplete="off" autocorrect="off" ' +
      'autocapitalize="off" spellcheck="false" placeholder="' + SEARCH_PLACEHOLDER + '">' +
      '<input type="hidden" name="options[prefix]" value="last">' +
      '<button type="submit">Search</button>';

    drawer.insertBefore(head, inner);
    drawer.insertBefore(form, inner);

    /* --- footer: account + favourites ------------------------------------ */
    var foot = el('div', 'sol-drawer__foot');
    var list = inner.querySelector('.mobile-menu-list');

    var accountItem = null;
    if (list) {
      Array.prototype.slice.call(list.children).forEach(function (li) {
        var link = li.querySelector('.menu-item-row > .main-menu-link');
        if (link && /my account/i.test(link.textContent)) accountItem = li;
      });
    }

    if (accountItem) {
      Array.prototype.slice.call(accountItem.querySelectorAll('.mobile-submenu a')).forEach(function (a) {
        var clone = a.cloneNode(true);
        clone.className = a.classList.contains('iwishlist-link') ? 'iwishlist-link' : '';
        foot.appendChild(clone);
      });
      accountItem.parentNode.removeChild(accountItem);
    }

    if (!foot.children.length) {
      var signIn = el('a', '', 'Sign in / My Account');
      signIn.href = rootUrl().replace(/\/$/, '') + '/account/login';
      var favs = el('a', 'iwishlist-link', 'Favorites');
      favs.href = 'javascript:void(0);';
      foot.appendChild(signIn);
      foot.appendChild(favs);
    }

    drawer.appendChild(foot);

    /* --- hairline + emphasis --------------------------------------------- */
    if (!list) return;

    var items = Array.prototype.slice.call(list.children).filter(function (n) {
      return n.tagName === 'LI';
    });
    var ruleIndex = -1;

    items.forEach(function (li, i) {
      var link = li.querySelector('.menu-item-row > .main-menu-link');
      if (!link) return;
      var title = link.textContent.trim();

      if (ruleIndex === -1 && /^curated$/i.test(title)) ruleIndex = i;
      if (title.toUpperCase() === 'SALE') li.classList.add('sol-menu-item--emphasis');
    });

    if (ruleIndex === -1 && items.length > 6) ruleIndex = 5;

    if (ruleIndex > 0 && items[ruleIndex]) {
      var rule = el('li', 'sol-menu-rule');
      rule.setAttribute('aria-hidden', 'true');
      list.insertBefore(rule, items[ruleIndex]);
    }

    makeParentRowsExpand(list);
  }

  /* A parent row (Shoes, Clothing, Accessories...) expands its submenu instead
     of jumping to the collection, so the whole line is one target. Because the
     row no longer links anywhere, a "View all X" link is added as the first
     child, otherwise the parent collection becomes unreachable from the menu. */
  function makeParentRowsExpand(list) {
    Array.prototype.slice.call(list.querySelectorAll('li.has-children')).forEach(function (li) {
      var row = li.querySelector('.menu-item-row');
      var link = row && row.querySelector('.main-menu-link');
      var toggle = row && row.querySelector('.submenu-toggle');
      var sub = li.querySelector('.mobile-submenu');
      if (!row || !link || !toggle || row.dataset.solRow === 'true') return;
      row.dataset.solRow = 'true';

      if (sub && !sub.querySelector('.sol-viewall-item')) {
        var item = el('li', 'mobile-menu-item main-menu-list-item sol-viewall-item');
        var wrap = el('div', 'menu-item-row');
        var a = el('a', 'menu-link main-menu-link');
        a.href = link.getAttribute('href') || '#';
        var link_text = link.textContent.trim();
        if(link_text == "Just In"){
          a.textContent = 'View all New Arrivals';
        }else{
            a.textContent = 'View all ' + link.textContent.trim();
        }
        wrap.appendChild(a);
        item.appendChild(wrap);
        sub.insertBefore(item, sub.firstChild);
      }

      row.addEventListener('click', function (event) {
        // Let the +/- button keep its own handler; dispatching to it from here
        // re-enters this listener, where this guard stops the loop.
        if (event.target.closest('.submenu-toggle')) return;
        event.preventDefault();
        toggle.click();
      });
    });
  }

  /* ------------------------------------------------------------------------
     2. HEADER ICONS AND SEARCH COPY
     ------------------------------------------------------------------------ */

  function moveSearchIcon() {
    var search = document.querySelector('.header-search-toggle');
    var disclosure = document.getElementById('menu-disclosure');
    if (!search || !disclosure || search.classList.contains('sol-header-search')) return;
    search.classList.add('sol-header-search');
    disclosure.parentElement.insertBefore(search, disclosure.nextSibling);
  }

  /* Account sits immediately left of the cart. Stock order puts the wishlist
     heart between them. */
  function orderHeaderIcons() {
    var icons = document.querySelector('.header-icons');
    if (!icons) return;
    var account = icons.querySelector('.accountMenuItem');
    var cart = icons.querySelector('#cart-icon');
    if (!account || !cart || account.nextElementSibling === cart) return;
    icons.insertBefore(account, cart);
  }

  /* The theme's search drawer ships a generic placeholder. The friendlier line
     belongs in the field people actually type into; the quick links heading
     above it is renamed in the header section's settings. */
  function setSearchDrawerCopy() {
    var input = document.querySelector('#search-drawer-input, .search-drawer input[type="search"]');
    if (input) input.placeholder = SEARCH_PLACEHOLDER;
  }

  /* ------------------------------------------------------------------------
     3. PRODUCT PAGE
     ------------------------------------------------------------------------ */

  /* Shopify's four-interest-free-instalments line renders wherever the theme
     put the payment-terms element, which on this product page is above the buy
     buttons. It reads as a payment option, so it belongs directly under the
     button that starts the payment. */
  function moveInstalments() {
    if (!document.body.classList.contains('template-product')) return;

    function move() {
      var terms = document.querySelector('.product-info .js-instalments-form, .product-info shopify-payment-terms');
      if (!terms) return false;
      var host = terms.classList.contains('js-instalments-form')
        ? terms
        : (terms.closest('.js-instalments-form') || terms);
      if (host.dataset.solMoved === 'true') return true;
      var anchor = document.querySelector('.product-info__block--buy-buttons');
      if (!anchor) return false;
      host.dataset.solMoved = 'true';
      anchor.parentNode.insertBefore(host, anchor.nextSibling);
      return true;
    }

    if (move()) return;

    var observer = new MutationObserver(function () {
      if (move()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () { observer.disconnect(); }, 8000);
  }

  /* The Rep AI question widget injects itself under the product gallery, where
     its 193px sat between the photo and the title. Moved below the buy block,
     which is a more natural place to ask a question anyway. It arrives after
     DOMContentLoaded, so watch for it rather than racing it.

     It anchors to the instalments line when that has already been moved, not
     to the button itself: both land at button.nextSibling, and whichever ran
     last would otherwise end up on top. Payment terms stay welded to the
     button; the widget follows underneath either way. */
  function relocateAiWidget() {
    if (!document.body.classList.contains('template-product')) return;

    function move() {
      var widget = document.getElementById('ai-recomm-media');
      if (!widget) return false;
      if (widget.dataset.solMoved === 'true') return true;
      var anchor = document.querySelector('.product-info .js-instalments-form[data-sol-moved="true"]') ||
                   document.querySelector('.product-info__block--buy-buttons');
      var info = document.querySelector('.product-info');
      if (!anchor && !info) return false;
      widget.dataset.solMoved = 'true';
      if (anchor) anchor.parentNode.insertBefore(widget, anchor.nextSibling);
      else info.appendChild(widget);
      return true;
    }

    if (move()) return;

    var observer = new MutationObserver(function () {
      if (move()) observer.disconnect();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    setTimeout(function () { observer.disconnect(); }, 10000);
  }

  /* Stamped renders the brand-reviews widget markup whether or not the brand
     has any reviews; with none, the widget lays out at zero height and the
     customer is left with a bare "Brand Reviews" heading over nothing. Measure
     the widget after it has had a chance to draw, and hide the whole section
     if it never takes up space. Re-checks for a few seconds because the app
     script loads late. (The empty star row under a product title is handled in
     CSS instead, from the count Stamped writes into the badge's aria-label.) */
  function hideEmptyBrandReviews() {
    var widget = document.getElementById('stamped-reviews-widget');
    if (!widget) return;
    var section = widget.closest('.shopify-section') || widget.parentElement;
    if (!section) return;

    var checks = 0;
    function check() {
      checks += 1;
      if (widget.getBoundingClientRect().height > 4) {
        section.classList.remove('sol-hidden');
        return;
      }
      section.classList.add('sol-hidden');
      if (checks < 8) setTimeout(check, 700);
    }
    check();
  }

  /* Sticky add to cart. Watches the theme's real add-to-cart button and
     forwards clicks to it, so variant logic, inventory rules, app hooks and
     analytics all keep firing. */
  function buildStickyAtc() {
    if (!document.body.classList.contains('template-product')) return;
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(min-width: 768px)').matches) return;

    var realBtn = document.querySelector(
      '.cc-main-product product-form button[name="add"], ' +
      '.main-product product-form button[name="add"], ' +
      'form[action*="/cart/add"] button[name="add"]'
    );
    if (!realBtn) return;

    // Read the visible price only. The theme keeps hidden unit-price and
    // 'Unavailable' nodes inside .price, so scraping the whole block would
    // print things like '$99.00 ( / ) Unavailable'.
    function priceText() {
      var scope = document.querySelector('.product-info__price') || document;
      var cur = scope.querySelector('.price__default .price__current');
      if (!cur) return '';
      var out = cur.textContent.trim();
      var was = scope.querySelector('.price__default .price__was');
      if (was && !was.hidden && was.textContent.trim()) out += '  ' + was.textContent.trim();
      return out.replace(/\s+/g, ' ');
    }

    var titleEl = document.querySelector('.product-title, .product-info h1, h1');

    var bar = el('div', 'sol-sticky-atc',
      '<div class="sol-sticky-atc__info">' +
      '<p class="sol-sticky-atc__title"></p>' +
      '<p class="sol-sticky-atc__price"></p>' +
      '</div><button type="button" class="sol-btn sol-btn--solid"></button>');

    bar.querySelector('.sol-sticky-atc__title').textContent = titleEl ? titleEl.textContent.trim() : '';

    var proxy = bar.querySelector('button');

    function sync() {
      var label = (realBtn.textContent || '').trim().replace(/\s+/g, ' ');
      proxy.textContent = label || 'Add to cart';
      proxy.disabled = realBtn.disabled;
      bar.querySelector('.sol-sticky-atc__price').textContent = priceText();
    }
    sync();

    proxy.addEventListener('click', function () {
      var picker = document.querySelector('variant-picker, .variant-picker');
      if (realBtn.disabled && picker) {
        picker.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      realBtn.click();
    });

    document.body.appendChild(bar);

    new IntersectionObserver(function (entries) {
      bar.classList.toggle('is-visible', !entries[0].isIntersecting);
    }, { rootMargin: '0px 0px -40px 0px' }).observe(realBtn);

    new MutationObserver(sync).observe(realBtn, {
      childList: true, subtree: true, attributes: true, attributeFilter: ['disabled']
    });
  }

  ready(function () {
    try { moveSearchIcon(); } catch (e) { /* never block the page */ }
    try { orderHeaderIcons(); } catch (e) { /* never block the page */ }
    try { buildDrawer(); } catch (e) { /* never block the page */ }
    try { setSearchDrawerCopy(); } catch (e) { /* never block the page */ }
    try { moveInstalments(); } catch (e) { /* never block the page */ }
    try { relocateAiWidget(); } catch (e) { /* never block the page */ }
    try { hideEmptyBrandReviews(); } catch (e) { /* never block the page */ }
    try { buildStickyAtc(); } catch (e) { /* never block the page */ }
  });

  // The theme editor re-renders sections; rebuild when it does.
  document.addEventListener('shopify:section:load', function () {
    var drawer = document.getElementById('main-menu');
    if (drawer) delete drawer.dataset.solEnhanced;
    try { moveSearchIcon(); } catch (e) {}
    try { orderHeaderIcons(); } catch (e) {}
    try { buildDrawer(); } catch (e) {}
    try { setSearchDrawerCopy(); } catch (e) {}
    try { moveInstalments(); } catch (e) {}
    try { relocateAiWidget(); } catch (e) {}
    try { hideEmptyBrandReviews(); } catch (e) {}
  });
})();
